import { Injectable } from '@angular/core';
import { DynamicQueryService } from '@services/analytics-services/core/dynamic-query.service';
import {
  AnalyticsRangeContext,
  DynamicQueryContext,
  RawQueryContext,
} from '@services/analytics-services/core';
import { map, tap, Observable } from 'rxjs';
import { summarize } from '@services/analytics-services/core/analytics.helper';
import { ProtectionService } from '@services/core-services/protection.service';

const LIMIT = 10000;

@Injectable({ providedIn: 'root' })
export class InsightsAnalyticsService {
  constructor(
    private readonly dynamicQueryService: DynamicQueryService,
    private readonly protectionService: ProtectionService
  ) {}

  getVisitorsCount(range: AnalyticsRangeContext) {
    const context: DynamicQueryContext = {
      query: {
        limit: LIMIT,
        offset: 0,
        aggregate: [
          {
            type: 'COUNT',
            alias: 'visitor_count',
            field_name: 'id',
          },
        ],
      },
      query_type: 'metrics',
      schema_name: 'ce_replica.`ce_customer.auth_anonymous_user`',
    };

    return this.dynamicQueryService.fetch(range, context);
  }

  getPageStat(
    range: AnalyticsRangeContext,
    timezone?: string
  ): Observable<{ event_name: string; events_count: number }[]> {
    const storeId = this.protectionService.getCurrentStoreSnapshot()?.id || 'default_store_id'; // Fallback if store is null

    const userTimezone = timezone || this.dynamicQueryService.getUserTimezone();
    const startTime = this.dynamicQueryService.toLocalTimezone(range.range.start, userTimezone);
    const endTime = this.dynamicQueryService.toLocalTimezone(range.range.end, userTimezone);

    const context: RawQueryContext = {
      raw_query: `
      SELECT 
        if(event_name='/', '/homepage', event_name) AS event_name, 
        COUNT(id) AS events_count 
      FROM ce_processed.analytics_event_log 
      WHERE event_type IN ('page') 
        AND store_id = '${storeId}'
        AND toTimeZone(created_at, '${userTimezone}') BETWEEN ${startTime} AND ${endTime}
        AND NOT (event_name LIKE '/checkout%' OR event_name LIKE '/profile%'
    )
      GROUP BY event_name 
      ORDER BY events_count DESC 
      LIMIT 100
    `,
    };

    return this.dynamicQueryService.fetchRaw(range, context).pipe(
      tap(({ list }) => list.sort((a: any, b: any) => b.events_count - a.events_count)),
      map(
        ({ list }) =>
          summarize(list, {
            labelKey: 'event_name',
            valueKey: 'events_count',
            limit: 10,
          }) as { event_name: string; events_count: number }[]
      )
    );
  }

  getWebsiteTraffic(range: AnalyticsRangeContext, timezone?: string) {
    // Get timezone from parameter or let DynamicQueryService handle it
    const userTimezone = timezone || this.dynamicQueryService.getUserTimezone();
    const startTime = this.dynamicQueryService.toLocalTimezone(range.range.start, userTimezone);
    const endTime = this.dynamicQueryService.toLocalTimezone(range.range.end, userTimezone);

    const context: RawQueryContext = {
      raw_query: `
        SELECT 
          dateName('weekday', toTimeZone(created_at, '${userTimezone}')) as day, 
          toHour(toTimeZone(created_at, '${userTimezone}')) as hour, 
          count(id) as total_event_count, 
          uniqExact(user_id) as unique_user_count 
        FROM ce_replica_analytics.analytics_event_log 
       WHERE toTimeZone(created_at, '${userTimezone}') BETWEEN ${startTime} AND ${endTime}
        GROUP BY 1, 2 
        ORDER BY day ASC, hour ASC
      `,
    };

    // Pass the timezone to fetchRaw so it's used in the date parameter replacement
    return this.dynamicQueryService.fetchRaw(range, context, timezone);
  }

  getServerEventsStat(range: AnalyticsRangeContext) {
    const context: DynamicQueryContext = {
      query: {
        limit: 25,
        offset: 0,
        filters: [
          {
            value: 'track',
            operator: 'equal',
            field_name: 'event_type',
          },
          {
            value: 'Server',
            operator: 'equal',
            field_name: 'channel',
          },
        ],
        aggregate: [
          {
            type: 'COUNT',
            alias: 'events_count',
            field_name: 'id',
          },
        ],
        group_by: {
          fields: 'event_name',
        },
      },
      query_type: 'stats',
      schema_name: 'ce_replica_analytics.analytics_event_log',
    };

    return this.dynamicQueryService.fetch(range, context).pipe(
      tap(({ list }) => list.sort((a: any, b: any) => b.events_count - a.events_count)),
      map(({ list }) =>
        summarize(list, {
          labelKey: 'event_name',
          valueKey: 'events_count',
          limit: 5,
        })
      )
    );
  }

  getVisitorsOverTime(range: AnalyticsRangeContext) {
    const context: DynamicQueryContext = {
      query: {
        limit: LIMIT,
        offset: 0,
        aggregate: [
          {
            type: 'COUNT',
            alias: 'visitor_count',
            field_name: 'id',
          },
        ],
      },
      query_type: 'metrics',
      schema_name: 'ce_replica.`ce_customer.auth_anonymous_user`',
    };

    return this.dynamicQueryService.fetch(range, context);
  }

  getSessionsStat(
    range: AnalyticsRangeContext,
    by: string = 'origin_source'
  ): Observable<{ origin_source: string; session_count: number }[]> {
    const context: DynamicQueryContext = {
      query: {
        limit: LIMIT,
        offset: 0,
        aggregate: [
          {
            type: 'COUNT',
            alias: 'session_count',
            field_name: 'id',
          },
        ],
        group_by: {
          fields: by,
        },
      },
      query_type: 'stats',
      schema_name: 'ce_replica_analytics.analytics_session',
    };

    return this.dynamicQueryService.fetch(range, context).pipe(
      tap(({ list }) => list.sort((a: any, b: any) => b.session_count - a.session_count)),
      map(
        ({ list }) =>
          summarize(list, {
            labelKey: by,
            valueKey: 'session_count',
            limit: 10,
          }) as { origin_source: string; session_count: number }[]
      )
    );
  }

  getVisitors(range: AnalyticsRangeContext, timezone?: string) {
    const storeId = this.protectionService.getCurrentStoreSnapshot()?.id || 'default_store_id'; // Fallback if store is null
    const userTimezone = timezone || this.dynamicQueryService.getUserTimezone();
    const startTime = this.dynamicQueryService.toLocalTimezone(range.range.start, userTimezone);
    const endTime = this.dynamicQueryService.toLocalTimezone(range.range.end, userTimezone);

    const context: RawQueryContext = {
      raw_query: `
        SELECT
            ael.source as source, 
            ael.utm_source, 
            ael.utm_campaign as campaign, 
            ael.utm_medium as medium,
            uniqExact(COALESCE(aui.user_ulid, ael.user_id)) AS unique_users 
        FROM
            ce_processed.analytics_event_log AS ael
            LEFT JOIN ce_replica."ce_customer.auth_user_anonymous_identity" AS aui 
          ON ael.user_id = aui.anonymous_ulid
        WHERE
            ael.event_type = 'page'
            AND ael.store_id = '${storeId}'
            AND toTimeZone(ael.created_at, '${userTimezone}') BETWEEN ${startTime} AND ${endTime}
        GROUP BY
           1,2,3,4
        ORDER BY
            unique_users DESC
      `,
    };

    return this.dynamicQueryService.fetchRaw(range, context);
  }

  getVisitorsByDevice(range: AnalyticsRangeContext, timezone?: string) {
    const storeId = this.protectionService.getCurrentStoreSnapshot()?.id || 'default_store_id'; // Fallback if store is null
    const userTimezone = timezone || this.dynamicQueryService.getUserTimezone();
    const startTime = this.dynamicQueryService.toLocalTimezone(range.range.start, userTimezone);
    const endTime = this.dynamicQueryService.toLocalTimezone(range.range.end, userTimezone);

    const context: RawQueryContext = {
      raw_query: `
        SELECT
            ael.device as device,
            uniqExact(COALESCE(aui.user_ulid, ael.user_id)) AS unique_users
        FROM
            ce_processed.analytics_event_log AS ael
            LEFT JOIN ce_replica."ce_customer.auth_user_anonymous_identity" AS aui ON ael.user_id = aui.anonymous_ulid
        WHERE
            ael.event_type = 'page'
            AND ael.store_id = '${storeId}'
            AND toTimeZone(ael.created_at, '${userTimezone}') BETWEEN ${startTime} AND ${endTime}
        GROUP BY
            ael.device
        ORDER BY
            unique_users DESC
      `,
    };

    return this.dynamicQueryService.fetchRaw(range, context);
  }

  getActiveUsers(range: AnalyticsRangeContext) {
    const storeId = this.protectionService.getCurrentStoreSnapshot()?.id || 'default_store_id'; // Fallback if store is null

    const context: RawQueryContext = {
      raw_query: `
          SELECT uniq(user_id) AS active_users  FROM ce_processed.analytics_event_log  WHERE created_at >= (now() - INTERVAL 180 SECOND) AND store_id = '${storeId}'
      `,
    };

    return this.dynamicQueryService.fetchRaw(range, context).pipe(
      map((response: { list: { active_users: number }[]; pagination?: any }) => {
        const activeUsers = response.list[0]?.active_users || 0;
        return { active_users: activeUsers };
      })
    );
  }
}
